<script setup lang="ts">
/**
 * Компонент CollapsibleCell - Адаптивная ячейка таблицы с возможностью сворачивания списков.
 *
 * Назначение:
 * - Компактное отображение длинных списков (требований, критериев и др.) в ячейках таблиц.
 * - Предотвращение растягивания строк таблицы по высоте за счет сворачивания элементов под кат.
 *
 * Особенности:
 * - Поддерживает на вход как массивы объектов [{ id, label }], так и массивы строк или сырые строки через запятую.
 * - Парсит коды элементов и выводит детальное описание во всплывающем тултипе (VTooltip).
 * - Управляет внутренним состоянием сворачивания/разворачивания индивидуально для каждой ячейки.
 */

import { computed, ref, watch } from 'vue'

interface Props {
  /** Входные данные: массив объектов с label/id, массив строк или строка через запятую */
  items: any[] | string | null | undefined
  /** Склоняемое имя типа элементов в родительном падеже (например, 'требований', 'критериев') */
  typeLabel: string
  /** Количество элементов, отображаемых до раскрытия списка */
  collapsedItemsCount?: number
  /** Дополнительные CSS-классы для элементов-кодов (например, 'font-weight-medium' или 'hover-underline-dashed') */
  codeClass?: string
  /** Дополнительные CSS-классы для кнопки раскрыть (например, 'font-weight-medium') */
  btnClass?: string
  /** Состояние развернутости списка */
  isExpanded?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsedItemsCount: 5,
  isExpanded: false,
})

const emit = defineEmits<{
  (e: 'update:isExpanded', value: boolean): void
}>()

/**
 * Состояние видимости элементов списка (true — развернут, false — свернут)
 */
const isExpanded = ref(props.isExpanded)

watch(() => props.isExpanded, val => {
  isExpanded.value = val
})

watch(isExpanded, val => {
  emit('update:isExpanded', val)
})

/**
 * Нормализованный массив элементов для рендеринга.
 * Приводит любые типы входных данных к общему формату: { id, code, text }
 */
const normalizedItems = computed(() => {
  if (!props.items) return []

  // Если пришла строка (например, из Архива), разбиваем по запятой
  if (typeof props.items === 'string') {
    return props.items
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map((s, index) => ({
        id: index,
        code: s,
        text: '',
      }))
  }

  // Если пришел массив объектов (например, из Реестра или Групп)
  if (Array.isArray(props.items)) {
    return props.items.map((item: any) => {
      const label = typeof item === 'string'
        ? item
        : (item.label || String(item.id || ''))

      // Отделяем код (первое слово) от расшифровки (остальной текст после пробела)
      const spaceIndex = label.indexOf(' ')
      const code = spaceIndex === -1 ? label : label.slice(0, spaceIndex)
      const text = spaceIndex === -1 ? '' : label.slice(spaceIndex + 1)

      return {
        id: item.id || Math.random(),
        code,
        text,
      }
    })
  }

  return []
})

const hasCollapsedPreview = computed(() => (props.collapsedItemsCount ?? 0) > 0)
const canExpand = computed(() =>
  normalizedItems.value.length > (props.collapsedItemsCount ?? 0),
)
const displayedItems = computed(() => {
  if (isExpanded.value || !hasCollapsedPreview.value) return normalizedItems.value

  return normalizedItems.value.slice(0, props.collapsedItemsCount)
})
const hiddenItemsCount = computed(() =>
  normalizedItems.value.length - displayedItems.value.length,
)
</script>

<template>
  <div v-if="normalizedItems.length" class="cell-wrap">
    <template v-if="!hasCollapsedPreview && !isExpanded">
      <span
        class="text-primary text-decoration-underline cursor-pointer"
        :class="[btnClass]"
        @click.stop="isExpanded = true"
      >
        Раскрыть {{ normalizedItems.length }} {{ typeLabel }}
      </span>
    </template>

    <template v-else>
      <span class="cell-codes">
        <template v-for="(item, index) in displayedItems" :key="item.id">
          <span class="d-inline-flex align-center">
            <VTooltip v-if="item.text" location="top" open-delay="200">
              <template #activator="{ props: tooltipProps }">
                <span
                  v-bind="tooltipProps"
                  class="cursor-help"
                  :class="[codeClass]"
                >
                  {{ item.code }}
                </span>
              </template>
              <span>{{ item.text }}</span>
            </VTooltip>
            <span v-else :class="codeClass">{{ item.code }}</span>
            <span v-if="index < displayedItems.length - 1" class="me-1">,</span>
          </span>
        </template>
      </span>

      <span
        v-if="canExpand && !isExpanded"
        class="cell-expand-btn text-primary cursor-pointer d-inline-flex align-center ms-1"
        :class="[btnClass]"
        @click.stop="isExpanded = true"
      >
        +{{ hiddenItemsCount }}
        <VIcon icon="ri-arrow-down-s-fill" size="14" />
      </span>

      <span
        v-else-if="canExpand"
        class="cell-expand-btn text-primary cursor-pointer d-inline-flex align-center ms-1"
        :class="[btnClass]"
        @click.stop="isExpanded = false"
      >
        Свернуть
        <VIcon icon="ri-arrow-up-s-fill" size="14" class="arrow-up" />
      </span>
    </template>
  </div>
  <span v-else>-</span>
</template>

<style scoped>
.cell-codes {
  display: contents;
}

.cursor-help {
  cursor: help;
}

/* Глиф ri-arrow-up-s-fill смещён к верху квадрата 24x24 — компенсируем, чтобы стрелка стояла по центру текста */
.arrow-up {
  transform: translateY(2px);
}
</style>
